import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Plus, X, Trash2, Pencil, AlertCircle, AlertTriangle, Pill, Package, Search, Loader2, RotateCw, Store } from 'lucide-react';
import {
    getDoctorMedicines,
    createDoctorMedicine,
    updateDoctorMedicine,
    deleteDoctorMedicine,
} from '../redux/medicineSlice';
import { serverReason } from '../components/patientTabs/prescriptionHelpers';
import PharmacyPickModal, { StockBadge } from '../components/modals/PharmacyPickModal';

const EMPTY_FORM = {
    name: '',
    brand: '',
    dose: '',
    description: '',
    purpose: '',
    isImportant: false,
    type: 'medicine',
};

const sanitizeText = (val = '') => val.replace(/[<>]/g, '');

// How long the search box waits after the last keystroke before asking the
// server — the catalogue is searched server-side, so every medicine can be
// found, not just the ones already loaded.
const SEARCH_DEBOUNCE_MS = 300;

// `doctorId` (prop) — whose list this is. The doctor's own Medicines page
// passes nothing and reads their id from the login; the tenant admin opens a
// doctor's list from that doctor's profile (owner, 12 Oct 2026: the admin adds,
// edits and deletes each doctor's medicines separately, on web and app alike —
// the server already allowed it, doctorMedicineRoutes.js doctorSetupRoles).
// `embedded` drops the page heading inside the profile tab.
function Medicines({ doctorId: managedDoctorId = null, embedded = false } = {}) {
    const { t, i18n } = useTranslation(['medicinesTab']);
    const lang = i18n?.language || 'en';
    const dispatch = useDispatch();
    const {
        medicines,
        loading,
        loadingMore,
        medicinesPage,
        medicinesTotalPages,
        medicinesTotal,
        medicinesError,
        loadMoreError,
        submitting,
        deletingId,
        medicinesSource,
    } = useSelector((state) => state.medicine);
    // Where this doctor's list comes from (owner, 12 Oct 2026): 'manual' — they
    // type entries; 'pharmacy' — they pick from the hospital pharmacy only;
    // 'both'. Sent by the server with the list.
    const source = medicinesSource || 'manual';
    const canType = source !== 'pharmacy';
    const canPick = source === 'pharmacy' || source === 'both';

    const userRole = localStorage.getItem('Role');
    const managing = userRole === 'admin' && Boolean(managedDoctorId);
    const readOnly = !(userRole === 'doctor' || managing);
    const doctorId = managing ? managedDoctorId : localStorage.getItem('ID');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState('');
    // The search text the list was last ASKED for (debounced from `search`).
    const [query, setQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [pickOpen, setPickOpen] = useState(false);
    // The entry being edited came from the pharmacy: its name, maker and type
    // follow the pharmacy and are not edited here.
    const [editingFromPharmacy, setEditingFromPharmacy] = useState(false);

    useEffect(() => {
        const next = search.trim();
        if (next === query) return undefined;
        const timer = setTimeout(() => setQuery(next), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search, query]);

    const loadPage = (page = 1) => {
        if (!doctorId) return;
        dispatch(getDoctorMedicines({ doctorId, type: filterType, search: query, page }));
    };

    useEffect(() => {
        loadPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorId, filterType, query, dispatch]);

    const hasMore = medicinesPage > 0 && medicinesPage < medicinesTotalPages;

    const validate = (data) => {
        const errs = {};
        // A pharmacy entry's name and maker are the pharmacy's (and may be
        // longer than a typed one is allowed to be) — not checked here.
        if (!editingFromPharmacy) {
            if (!data.name?.trim()) errs.name = t('medicinesTab.name_required', 'Name is required');
            else if (data.name.length > 100) errs.name = t('medicinesTab.name_too_long', 'Name too long');
            if (data.brand && data.brand.length > 80) errs.brand = t('medicinesTab.brand_too_long', 'Brand too long');
        }
        if (data.dose && data.dose.length > 50) errs.dose = t('medicinesTab.dose_too_long', 'Dose too long');
        if (data.purpose && data.purpose.length > 150) errs.purpose = t('medicinesTab.purpose_too_long', 'Purpose too long');
        if (data.description && data.description.length > 500) errs.description = t('medicinesTab.description_too_long', 'Description too long');
        return errs;
    };

    const handleOpenAdd = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setIsModalOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditingId(item.medicineId);
        setEditingFromPharmacy(Boolean(item.pharmacy));
        setForm({
            name: item.name || '',
            brand: item.brand || '',
            dose: item.dose || '',
            description: item.description || '',
            purpose: item.purpose || '',
            isImportant: !!item.isImportant,
            type: item.type || 'medicine',
        });
        setErrors({});
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setEditingId(null);
        setEditingFromPharmacy(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    const handleChange = (field, value) => {
        const cleaned = typeof value === 'string' ? sanitizeText(value) : value;
        setForm((prev) => ({ ...prev, [field]: cleaned }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const errs = validate(form);
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }

        const payload = form.type === 'product'
            ? { ...form, dose: '', isImportant: false }
            : form;

        try {
            if (editingId) {
                const res = await dispatch(updateDoctorMedicine({ medicineId: editingId, payload }));
                if (updateDoctorMedicine.fulfilled.match(res)) {
                    toast.success(t('medicinesTab.updated_success', 'Medicine updated'));
                    handleClose();
                } else {
                    toast.error(serverReason(res.payload, t('medicinesTab.update_failed', 'Update failed'), lang));
                }
            } else {
                const res = await dispatch(createDoctorMedicine({ doctorId, ...payload }));
                if (createDoctorMedicine.fulfilled.match(res)) {
                    toast.success(t('medicinesTab.created_success', 'Medicine added'));
                    handleClose();
                } else {
                    toast.error(serverReason(res.payload, t('medicinesTab.create_failed', 'Create failed'), lang));
                }
            }
        } catch (err) {
            console.error(err);
            toast.error(t('medicinesTab.unexpected_error', 'Something went wrong'));
        }
    };

    const handleDelete = async (item) => {
        const result = await Swal.fire({
            title: t('medicinesTab.confirm_delete_title', 'Delete medicine?'),
            text: t('medicinesTab.confirm_delete_text', 'This will remove "{{name}}" from your list.', { name: item.name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('medicinesTab.delete_confirm', 'Yes, delete'),
            cancelButtonText: t('medicinesTab.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;
        const res = await dispatch(deleteDoctorMedicine(item.medicineId));
        if (deleteDoctorMedicine.fulfilled.match(res)) {
            toast.success(t('medicinesTab.deleted_success', 'Medicine deleted'));
        } else {
            toast.error(serverReason(res.payload, t('medicinesTab.delete_failed', 'Delete failed'), lang));
        }
    };

    return (
        <div data-testid="medicines-page" className={embedded ? '' : 'bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto'}>
            {!embedded && (
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('medicinesTab.heading', 'Medicines & Products')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    {t('medicinesTab.subheading', 'Manage the medicines and products you commonly suggest to your patients.')}
                </p>
            </div>
            )}

            <div className="space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex gap-2 flex-wrap">
                        {['all', 'medicine', 'product'].map((tp) => (
                            <button
                                key={tp}
                                type="button"
                                onClick={() => setFilterType(tp)}
                                className={`px-3 py-2 rounded-xl text-sm font-medium border transition cursor-pointer ${
                                    filterType === tp
                                        ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ABDBD]'
                                }`}
                            >
                                {tp === 'all'
                                    ? t('medicinesTab.filter_all', 'All')
                                    : tp === 'medicine'
                                    ? t('medicinesTab.filter_medicine', 'Medicines')
                                    : t('medicinesTab.filter_product', 'Products')}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3 lg:ml-auto flex-wrap sm:flex-nowrap">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                data-testid="medicines-search-input"
                                value={search}
                                onChange={(e) => setSearch(sanitizeText(e.target.value))}
                                placeholder={t('medicinesTab.search_placeholder', 'Search by name, brand or purpose')}
                                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-[#4ABDBD]"
                            />
                        </div>
                        {!readOnly && canPick && (
                            <button
                                type="button"
                                data-testid="medicines-page-pick-btn"
                                onClick={() => setPickOpen(true)}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition cursor-pointer whitespace-nowrap ${
                                    canType
                                        ? 'border border-[#4ABDBD] text-[#2D8A8A] bg-white hover:bg-[#4ABDBD]/10'
                                        : 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white shadow hover:shadow-md'
                                }`}
                            >
                                <Store size={18} /> {t('medicinesTab.pick_btn', 'Pick from pharmacy')}
                            </button>
                        )}
                        {!readOnly && canType && (
                            <button
                                type="button"
                                data-testid="medicines-page-add-btn"
                                onClick={handleOpenAdd}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold shadow hover:shadow-md transition cursor-pointer whitespace-nowrap"
                            >
                                <Plus size={18} /> {t('medicinesTab.add_btn', 'Add Medicine')}
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500" data-testid="medicines-loading">
                        {t('medicinesTab.loading', 'Loading medicines...')}
                    </div>
                ) : medicinesError ? (
                    // Loading, empty and error are three different things: a list
                    // that could not be loaded is NOT an empty catalogue.
                    <div role="alert" data-testid="medicines-load-error" className="py-10 px-4 flex flex-col items-center gap-2 text-center bg-red-50/60 rounded-2xl border border-red-100">
                        <AlertTriangle className="text-red-400" size={32} />
                        <p className="font-semibold text-red-700">
                            {t('medicinesTab.load_failed', 'Your medicines could not be loaded.')}
                        </p>
                        <p className="text-sm text-red-600" data-testid="medicines-load-error-reason">
                            {serverReason(medicinesError, t('medicinesTab.load_failed_hint', 'Check your connection and try again.'), lang)}
                        </p>
                        <button
                            type="button"
                            data-testid="medicines-retry-btn"
                            onClick={() => loadPage(1)}
                            className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-red-200 bg-white text-red-700 text-sm font-semibold hover:bg-red-50 transition cursor-pointer"
                        >
                            <RotateCw size={14} /> {t('medicinesTab.retry', 'Retry')}
                        </button>
                    </div>
                ) : medicines.length === 0 ? (
                    <div className="py-10 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200" data-testid="medicines-empty">
                        {query ? (
                            <Search className="mx-auto mb-2 text-gray-300" size={36} />
                        ) : (
                            <Pill className="mx-auto mb-2 text-gray-300" size={36} />
                        )}
                        <p className="text-gray-500">
                            {query
                                ? t('medicinesTab.no_match', 'No medicine matches “{{query}}”.', { query })
                                : filterType === 'product'
                                ? t('medicinesTab.empty_products', 'No products in your catalogue yet.')
                                : filterType === 'medicine'
                                ? t('medicinesTab.empty_medicines', 'No medicines in your catalogue yet.')
                                : !canType
                                ? t('medicinesTab.empty_pharmacy', 'Nothing on your list yet. Pick what you prescribe from the pharmacy.')
                                : t('medicinesTab.empty', 'No medicines yet. Add your first one.')}
                        </p>
                    </div>
                ) : (
                    <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {medicines.map((m) => {
                            const isProduct = m.type === 'product';
                            const isDeleting = deletingId === m.medicineId;
                            const accent = isProduct
                                ? { ring: 'ring-amber-200/60', cardBg: 'bg-gradient-to-br from-amber-50 via-orange-50/40 to-white', tile: 'from-amber-400 to-orange-400 text-white', dot: 'bg-amber-400', label: t('medicinesTab.type_product', 'Product') }
                                : { ring: 'ring-[#4ABDBD]/25', cardBg: 'bg-gradient-to-br from-[#E8F7F7] via-[#F3FBFB] to-white', tile: 'from-[#4ABDBD] to-[#3AA8A8] text-white', dot: 'bg-[#4ABDBD]', label: t('medicinesTab.type_medicine', 'Medicine') };
                            return (
                                <div
                                    key={m.medicineId}
                                    data-testid={`medicine-card-${m.medicineId}`}
                                    className={`group relative rounded-2xl ${accent.cardBg} p-5 ring-1 ${accent.ring} shadow-[0_4px_18px_-8px_rgba(74,189,189,0.18)] hover:shadow-[0_12px_30px_-10px_rgba(74,189,189,0.4)] hover:-translate-y-0.5 transition-all duration-300`}
                                >
                                    {!readOnly && (
                                        // Always visible: a tablet or phone has no hover.
                                        <div className="absolute top-3 right-3 flex items-center gap-1" data-testid={`medicine-card-actions-${m.medicineId}`}>
                                            <button
                                                type="button"
                                                onClick={() => handleOpenEdit(m)}
                                                disabled={isDeleting}
                                                className="w-7 h-7 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-[#4ABDBD] hover:border-[#4ABDBD] shadow-sm flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                                aria-label={t('medicinesTab.edit', 'Edit')}
                                            >
                                                <Pencil size={13} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(m)}
                                                disabled={isDeleting}
                                                className="w-7 h-7 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 shadow-sm flex items-center justify-center cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                                aria-label={t('medicinesTab.delete', 'Delete')}
                                            >
                                                {isDeleting ? (
                                                    <Loader2 size={13} className="animate-spin text-red-500" />
                                                ) : (
                                                    <Trash2 size={13} />
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 pr-16">
                                        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${accent.tile} flex items-center justify-center shrink-0 shadow-sm`}>
                                            {isProduct ? <Package size={22} strokeWidth={2.2} /> : <Pill size={22} strokeWidth={2.2} />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`w-1.5 h-1.5 rounded-full ${accent.dot}`} />
                                                <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                                                    {accent.label}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-800 leading-tight truncate" title={m.name}>
                                                {m.name}
                                            </h3>
                                            {m.brand && (
                                                <p className="text-sm text-gray-500 mt-0.5 truncate">{m.brand}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 flex-wrap mt-4">
                                        {!isProduct && m.dose && (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                                                {m.dose}
                                            </span>
                                        )}
                                        {!isProduct && m.isImportant && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-semibold">
                                                <AlertCircle size={11} /> {t('medicinesTab.important', 'Important')}
                                            </span>
                                        )}
                                    </div>
                                    {m.pharmacy && (
                                        <div className="flex items-center gap-1.5 flex-wrap mt-3" data-testid={`medicine-card-pharmacy-${m.medicineId}`}>
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#4ABDBD]/40 text-[#2D8A8A] text-[11px] font-semibold">
                                                <Store size={11} /> {t('medicinesTab.from_pharmacy', 'Pharmacy')}
                                            </span>
                                            <StockBadge stock={m.pharmacy.stock} status={m.pharmacy.status} t={t} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex flex-col items-center gap-2 pt-2" data-testid="medicines-paging">
                        {medicinesTotal > 0 && (
                            <p className="text-xs text-gray-400" data-testid="medicines-count">
                                {t('medicinesTab.showing_count', 'Showing {{shown}} of {{total}}', { shown: medicines.length, total: medicinesTotal })}
                            </p>
                        )}
                        {loadMoreError && (
                            <p role="alert" className="text-sm text-red-600" data-testid="medicines-load-more-error">
                                {serverReason(loadMoreError, t('medicinesTab.load_more_failed', 'More medicines could not be loaded.'), lang)}
                            </p>
                        )}
                        {hasMore && (
                            <button
                                type="button"
                                data-testid="medicines-load-more-btn"
                                onClick={() => loadPage(medicinesPage + 1)}
                                disabled={loadingMore}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] text-sm font-semibold bg-white hover:bg-[#4ABDBD]/10 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loadingMore && <Loader2 size={14} className="animate-spin" />}
                                {loadingMore
                                    ? t('medicinesTab.loading_more', 'Loading…')
                                    : loadMoreError
                                    ? t('medicinesTab.retry', 'Retry')
                                    : t('medicinesTab.load_more', 'Load more')}
                            </button>
                        )}
                    </div>
                    </>
                )}
            </div>

            {pickOpen && (
                <PharmacyPickModal
                    doctorId={doctorId}
                    onClose={() => {
                        setPickOpen(false);
                        loadPage(1);
                    }}
                />
            )}

            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="medicine-modal-title"
                    id="medicine-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="medicine-modal-title"
                                data-testid="modal-title"
                            >
                                {editingId
                                    ? t('medicinesTab.edit_title', 'Edit Medicine')
                                    : t('medicinesTab.add_title', 'Add Medicine')}
                            </h2>
                            <button
                                type="button"
                                id="medicine-modal-close-button"
                                onClick={handleClose}
                                aria-label={t('medicinesTab.close', 'Close')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={submitting}
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="medicine-form" onSubmit={handleSubmit} data-testid="medicine-form" className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        {t('medicinesTab.type_label', 'Type')}
                                    </label>
                                    {editingId ? (
                                        <div
                                            data-testid="medicine-type-static"
                                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 text-[#3AA8A8] text-sm font-semibold"
                                        >
                                            {form.type === 'product' ? (
                                                <Package size={16} className="text-amber-500" />
                                            ) : (
                                                <Pill size={16} className="text-[#4ABDBD]" />
                                            )}
                                            {form.type === 'product'
                                                ? t('medicinesTab.type_product', 'Product')
                                                : t('medicinesTab.type_medicine', 'Medicine')}
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            {[
                                                { key: 'medicine', label: t('medicinesTab.type_medicine', 'Medicine') },
                                                { key: 'product', label: t('medicinesTab.type_product', 'Product') },
                                            ].map((opt) => (
                                                <button
                                                    key={opt.key}
                                                    type="button"
                                                    onClick={() => handleChange('type', opt.key)}
                                                    disabled={submitting}
                                                    className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium transition cursor-pointer disabled:cursor-not-allowed ${
                                                        form.type === opt.key
                                                            ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ABDBD]'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="medicine-name">
                                        {t('medicinesTab.name_label', 'Name')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="medicine-name"
                                        type="text"
                                        value={form.name}
                                        maxLength={100}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        disabled={submitting || editingFromPharmacy}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${
                                            errors.name
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300 hover:border-[#4ABDBD]'
                                        }`}
                                    />
                                    {errors.name && <p className="mt-1 text-xs text-red-500 font-medium">{errors.name}</p>}
                                    {editingFromPharmacy && (
                                        <p className="mt-1 text-xs text-gray-500" data-testid="medicine-from-pharmacy-note">
                                            {t('medicinesTab.from_pharmacy_note', 'From the pharmacy list: the name, maker and type follow the pharmacy.')}
                                        </p>
                                    )}
                                </div>

                                <div className={`grid grid-cols-1 gap-4 ${form.type === 'product' ? '' : 'sm:grid-cols-2'}`}>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="medicine-brand">
                                            {t('medicinesTab.brand_label', 'Brand')}
                                        </label>
                                        <input
                                            id="medicine-brand"
                                            type="text"
                                            value={form.brand}
                                            maxLength={80}
                                            onChange={(e) => handleChange('brand', e.target.value)}
                                            disabled={submitting || editingFromPharmacy}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${
                                                errors.brand
                                                    ? 'border-red-500 bg-red-50'
                                                    : 'border-gray-300 hover:border-[#4ABDBD]'
                                            }`}
                                        />
                                        {errors.brand && <p className="mt-1 text-xs text-red-500 font-medium">{errors.brand}</p>}
                                    </div>
                                    {form.type !== 'product' && (
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="medicine-dose">
                                                {t('medicinesTab.dose_label', 'Dose')}
                                            </label>
                                            <input
                                                id="medicine-dose"
                                                type="text"
                                                value={form.dose}
                                                maxLength={50}
                                                placeholder="e.g. 500mg"
                                                onChange={(e) => handleChange('dose', e.target.value)}
                                                disabled={submitting}
                                                className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${
                                                    errors.dose
                                                        ? 'border-red-500 bg-red-50'
                                                        : 'border-gray-300 hover:border-[#4ABDBD]'
                                                }`}
                                            />
                                            {errors.dose && <p className="mt-1 text-xs text-red-500 font-medium">{errors.dose}</p>}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="medicine-purpose">
                                        {t('medicinesTab.purpose_label', 'Purpose')}
                                    </label>
                                    <input
                                        id="medicine-purpose"
                                        type="text"
                                        value={form.purpose}
                                        maxLength={150}
                                        onChange={(e) => handleChange('purpose', e.target.value)}
                                        disabled={submitting}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${
                                            errors.purpose
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300 hover:border-[#4ABDBD]'
                                        }`}
                                    />
                                    {errors.purpose && <p className="mt-1 text-xs text-red-500 font-medium">{errors.purpose}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="medicine-description">
                                        {t('medicinesTab.description_label', 'Description')}
                                    </label>
                                    <textarea
                                        id="medicine-description"
                                        rows="4"
                                        value={form.description}
                                        maxLength={500}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        disabled={submitting}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 resize-none ${
                                            errors.description
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300 hover:border-[#4ABDBD]'
                                        }`}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">{form.description?.length || 0}/500</p>
                                    {errors.description && <p className="mt-1 text-xs text-red-500 font-medium">{errors.description}</p>}
                                </div>

                                {form.type !== 'product' && (
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={form.isImportant}
                                            onChange={(e) => handleChange('isImportant', e.target.checked)}
                                            disabled={submitting}
                                            className="w-4 h-4 accent-[#4ABDBD]"
                                        />
                                        <span className="text-sm text-gray-700">
                                            {t('medicinesTab.important_label', 'Mark as important drug')}
                                        </span>
                                    </label>
                                )}
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="medicine-form-cancel-button"
                                onClick={handleClose}
                                disabled={submitting}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                data-testid="cancel-button"
                            >
                                {t('medicinesTab.cancel', 'Cancel')}
                            </button>
                            <button
                                type="submit"
                                id="medicine-form-submit-button"
                                form="medicine-form"
                                disabled={submitting}
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                                data-testid="submit-button"
                            >
                                {submitting && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {submitting
                                    ? t('medicinesTab.saving', 'Saving...')
                                    : editingId
                                    ? t('medicinesTab.save', 'Save')
                                    : t('medicinesTab.add', 'Add')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Medicines;
